/**
 * Finance → Economic Schedules → Pending Approval.
 * Migrated or proposed economics never become financial truth silently: a native schedule needs
 * evidence and a different approver (maker/checker); corrections supersede, never edit.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, FileUp, GitBranch, RotateCcw, Send, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import useAuthStore from '../lib/auth-store';
import {
  Card, FinanceOnly, PageHeader, StatusPill, TenantSelect, btnGhost, btnPrimary, btnSmall,
  downloadFile, errorText, inputCls, rate, useLatestRequest, useTenantScope,
} from '../components/finance/financeUi';

const FILTERS = [
  ['pending', 'Pending approval', 'draft,submitted'],
  ['returned', 'Returned', 'returned'],
  ['approved', 'Approved, not active', 'approved'],
  ['history', 'Active & superseded', 'active,superseded,superseded_correction'],
];
const when = (v) => (v ? new Date(v).toLocaleDateString() : '—');

const Field = ({ label, value, testid }) => (
  <div className="min-w-0">
    <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
    <dd className="mt-0.5 truncate text-sm font-medium" data-testid={testid}>{value || '—'}</dd>
  </div>
);

const EconomicsTable = ({ row }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid="schedule-economics-table">
      <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
        <tr>{['Fee', 'Unit', 'Parent cost', row.schedule.schedule_type === 'native_processor' ? 'Native cost' : 'Assigned cost', 'House spread']
          .map((h) => <th key={h} className="px-5 py-2.5 font-medium">{h}</th>)}</tr>
      </thead>
      <tbody>
        {row.comparison.lines.map((l) => (
          <tr key={l.fee_code} className="border-t" data-testid={`econ-line-${l.fee_code}`}>
            <td className="px-5 py-2.5 font-medium">{l.label || l.fee_code}</td>
            <td className="px-5 py-2.5 text-xs text-muted-foreground">{l.unit.replace(/_/g, ' ')}</td>
            <td className="px-5 py-2.5 tabular-nums">{rate(l.parent_cost)}</td>
            <td className="px-5 py-2.5 tabular-nums">{l.error ? <span className="text-rose-600 text-xs">{l.error}</span> : rate(l.assigned_cost)}</td>
            <td className={`px-5 py-2.5 tabular-nums ${l.spread > 0 ? 'text-emerald-700' : l.spread < 0 ? 'text-rose-600' : ''}`}>
              {l.spread == null ? '—' : `${l.spread > 0 ? '+' : ''}${rate(l.spread)}`}
            </td>
          </tr>
        ))}
        {row.comparison.missing_from_child.map((code) => (
          <tr key={code} className="border-t text-amber-700"><td className="px-5 py-2.5" colSpan={5}>
            Parent fee “{code}” has no line in this schedule</td></tr>
        ))}
      </tbody>
    </table>
  </div>
);

function Evidence({ row, onChanged }) {
  const v = row.version;
  const native = row.schedule.schedule_type === 'native_processor';
  const [form, setForm] = useState({ source_reference: v.source_reference || '', source_date: '',
                                     economic_program: v.economic_program || row.schedule.variant || '' });
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true);
    try {
      const res = await fetch(`${api.baseURL}/financials/schedule-versions/${v.version_id}/source-document`, {
        method: 'POST', headers: { Authorization: api.getHeaders().Authorization }, body: fd });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success('Source document attached');
      onChanged();
    } catch (err) { toast.error(errorText(err)); } finally { setBusy(false); }
  };
  const submit = async () => {
    setBusy(true);
    try {
      await api.request(`/financials/schedule-versions/${v.version_id}/submit`,
                        { method: 'POST', body: JSON.stringify(form) });
      toast.success('Submitted for approval');
      onChanged();
    } catch (err) { toast.error(errorText(err)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 border-t px-5 py-4" data-testid="schedule-submit-form">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        Evidence {native ? '(required for native economics)' : ''}
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        <input className={inputCls} placeholder="Authoritative reference" value={form.source_reference}
               onChange={(e) => setForm({ ...form, source_reference: e.target.value })}
               data-testid="submit-source-reference" />
        <input type="date" className={inputCls} value={form.source_date}
               onChange={(e) => setForm({ ...form, source_date: e.target.value })} data-testid="submit-source-date" />
        <input className={inputCls} placeholder="Economic program (e.g. standard)" value={form.economic_program}
               onChange={(e) => setForm({ ...form, economic_program: e.target.value })}
               data-testid="submit-economic-program" />
      </div>
      <div className="flex flex-wrap gap-2">
        <label className={`${btnGhost} cursor-pointer`} data-testid="attach-source-document">
          <FileUp className="h-4 w-4" />{v.source_document ? 'Replace document' : 'Attach source document'}
          <input type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        </label>
        <button type="button" className={btnPrimary} disabled={busy} onClick={submit} data-testid="submit-for-approval-btn">
          <Send className="h-4 w-4" />Submit for approval
        </button>
      </div>
    </div>
  );
}

function Correction({ row, onDone }) {
  const [items, setItems] = useState(row.comparison.lines.map((l, i) => ({
    ...row.items?.[i], fee_code: l.fee_code, label: l.label, unit: l.unit, derivation: l.derivation,
    amount: row.items?.[i]?.amount ?? 0, markup_amount: row.items?.[i]?.markup_amount ?? 0 })));
  const [reason, setReason] = useState('');
  const save = async () => {
    try {
      await api.request(`/financials/schedule-versions/${row.version.version_id}/correct`, {
        method: 'POST', body: JSON.stringify({ items, reason }) });
      toast.success('Corrected version drafted; the original is kept as superseded');
      onDone(true);
    } catch (err) { toast.error(errorText(err)); }
  };
  const field = (it) => (it.derivation === 'explicit' ? 'amount' : 'markup_amount');
  return (
    <div className="space-y-3 border-t bg-muted/30 px-5 py-4" data-testid="schedule-correction-form">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">New corrected version</p>
      {items.map((it, i) => (
        <div key={it.fee_code} className="grid grid-cols-[1fr_9rem] items-center gap-3 text-sm">
          <span>{it.label || it.fee_code} <span className="text-xs text-muted-foreground">({field(it).replace('_', ' ')})</span></span>
          <input type="number" step="0.0001" className={inputCls} value={it[field(it)]}
                 data-testid={`correct-item-${it.fee_code}`}
                 onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, [field(x)]: parseFloat(e.target.value) || 0 } : x)))} />
        </div>
      ))}
      <input className={inputCls} placeholder="Reason for correction (required)" value={reason}
             onChange={(e) => setReason(e.target.value)} data-testid="correct-reason" />
      <div className="flex gap-2">
        <button type="button" className={btnPrimary} onClick={save} disabled={!reason.trim()} data-testid="correct-save-btn">Create corrected version</button>
        <button type="button" className={btnGhost} onClick={() => onDone(false)} data-testid="correct-cancel-btn">Cancel</button>
      </div>
    </div>
  );
}

function Detail({ row, user, onChanged }) {
  const v = row.version;
  const [mode, setMode] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setMode(''); setText(''); }, [v.version_id]);
  const isMaker = [v.created_by, v.submitted_by].includes(user?.id || user?.user_id);
  const act = async (path, body, message) => {
    setBusy(true);
    try {
      const result = await api.request(`/financials/schedule-versions/${v.version_id}/${path}`,
                                       { method: 'POST', body: JSON.stringify(body || {}) });
      const n = result?.reevaluated_periods?.length;
      toast.success(n ? `${message} · ${n} blocked period(s) re-evaluated` : message);
      setMode('');
      onChanged();
    } catch (err) { toast.error(errorText(err)); } finally { setBusy(false); }
  };
  const native = row.schedule.schedule_type === 'native_processor';
  const canApprove = (v.status === 'submitted' || (v.status === 'draft' && !row.requires_maker_checker))
    && (!native || user?.role === 'super_admin');
  const editable = ['draft', 'submitted', 'returned', 'approved'].includes(v.status);
  return (
    <Card testid="schedule-detail" title={row.schedule.name}
          aside={<StatusPill value={row.verification} testid="schedule-verification-status" />}>
      <dl className="grid gap-4 px-5 py-4 sm:grid-cols-3">
        <Field label="Type" value={row.schedule.schedule_type === 'native_processor' ? 'NATIVE_PROCESSOR' : 'ASSIGNED_DOWNSTREAM'} testid="detail-type" />
        <Field label="Economic owner" value={row.schedule.schedule_type === 'native_processor' ? row.owner_name : row.assignor_name} />
        <Field label="Assigned to" value={row.assignee_name} testid="detail-assignee" />
        <Field label="Processor" value={row.schedule.provider_slug} />
        <Field label="Parent" value={row.parent_name} testid="detail-parent" />
        <Field label="Effective" value={when(v.effective_from)} />
        <Field label="Source" value={row.source} testid="detail-source" />
        <Field label="Migration" value={row.migration_status || 'n/a'} />
        <Field label="Program" value={v.economic_program || row.schedule.variant} />
        <Field label="Fees / differences" value={`${row.fee_count} fee${row.fee_count === 1 ? '' : 's'} · ${row.comparison.differences} differ${row.comparison.differences === 1 ? 's' : ''}`} />
        <Field label="Created by" value={row.created_by_name} />
        <Field label="Approver" value={row.approved_by_name || (row.requires_maker_checker ? 'Different person required' : 'Any authorised admin')} />
      </dl>
      <EconomicsTable row={row} />
      {(v.source_reference || v.source_document) && (
        <div className="flex flex-wrap items-center gap-3 border-t px-5 py-3 text-sm" data-testid="schedule-evidence">
          <ShieldCheck className="h-4 w-4 text-emerald-600" />
          <span>{v.source_reference || 'Document attached'}{v.source_date ? ` · dated ${when(v.source_date)}` : ''}</span>
          {v.source_document && (
            <button type="button" className={btnSmall} data-testid="download-source-document"
                    onClick={() => downloadFile(`/financials/schedule-versions/${v.version_id}/source-document`,
                                                v.source_document.filename || 'source').catch((e) => toast.error(errorText(e)))}>
              {v.source_document.filename}
            </button>
          )}
        </div>
      )}
      {v.return_reason && (
        <p className="border-t px-5 py-3 text-sm text-rose-600" data-testid="schedule-return-reason">Returned: {v.return_reason}</p>
      )}
      {v.status === 'draft' && <Evidence row={row} onChanged={onChanged} />}
      {mode === 'correct' && <Correction row={row} onDone={(ok) => { setMode(''); if (ok) onChanged(); }} />}
      {mode === 'return' && (
        <div className="flex gap-2 border-t px-5 py-4">
          <input className={inputCls} placeholder="What must be corrected?" value={text}
                 onChange={(e) => setText(e.target.value)} data-testid="return-reason-input" />
          <button type="button" className={btnPrimary} disabled={!text.trim() || busy}
                  onClick={() => act('return', { reason: text }, 'Returned for correction')} data-testid="return-confirm-btn">Return</button>
        </div>
      )}
      {mode === 'approve' && (
        <div className="space-y-3 border-t bg-primary/5 px-5 py-4" data-testid="approve-confirm-panel">
          <p className="text-sm font-medium">Approve and activate v{v.version}? It becomes live financial truth from {when(v.effective_from)}.</p>
          <input className={inputCls} placeholder="Approval note (optional)" value={text}
                 onChange={(e) => setText(e.target.value)} data-testid="approve-note-input" />
          <div className="flex gap-2">
            <button type="button" className={btnPrimary} disabled={busy} data-testid="approve-confirm-btn"
                    onClick={() => act('approve-and-activate', { note: text }, 'Approved and activated')}>Yes, approve &amp; activate</button>
            <button type="button" className={btnGhost} onClick={() => setMode('')} data-testid="approve-cancel-btn">Cancel</button>
          </div>
        </div>
      )}
      {editable && (
      <div className="flex flex-wrap items-center gap-2 border-t px-5 py-4">
        {canApprove && (
          <button type="button" className={btnPrimary} disabled={busy || (row.requires_maker_checker && isMaker)}
                  onClick={() => { setText(''); setMode('approve'); }} data-testid="approve-activate-btn"
                  title={row.requires_maker_checker && isMaker ? 'You created or submitted this version' : ''}>
            <CheckCircle2 className="h-4 w-4" />Approve &amp; activate
          </button>
        )}
        {v.status === 'approved' && (
          <button type="button" className={btnPrimary} disabled={busy} data-testid="activate-btn"
                  onClick={() => act('activate', {}, 'Activated')}>Activate</button>
        )}
        {v.status === 'submitted' && (
          <button type="button" className={btnGhost} onClick={() => { setText(''); setMode('return'); }} data-testid="return-btn">
            <RotateCcw className="h-4 w-4" />Return for correction
          </button>
        )}
        {['draft', 'submitted', 'returned', 'approved'].includes(v.status) && (
          <button type="button" className={btnGhost} onClick={() => setMode('correct')} data-testid="edit-new-version-btn">
            <GitBranch className="h-4 w-4" />Edit new version
          </button>
        )}
        {row.requires_maker_checker && isMaker && canApprove && (
          <span className="text-xs text-muted-foreground" data-testid="maker-checker-note">Maker/checker: a different approver is required.</span>
        )}
      </div>
      )}
      <ol className="space-y-1 border-t px-5 py-4 text-xs text-muted-foreground" data-testid="schedule-history">
        {(v.history || []).map((h, i) => (
          <li key={i}>{new Date(h.at).toLocaleString()} · {h.action.replace(/_/g, ' ')}{h.note ? ` — ${h.note}` : ''}</li>
        ))}
      </ol>
    </Card>
  );
}

export default function ScheduleApprovalsPage() {
  const { user } = useAuthStore();
  const scope = useTenantScope(user);
  const [filter, setFilter] = useState('pending');
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState('');
  const [settings, setSettings] = useState(null);
  const nextRequest = useLatestRequest();
  const load = useCallback(async () => {
    if (!scope.ready) return;
    const isCurrent = nextRequest();
    const status = FILTERS.find((f) => f[0] === filter)[2];
    try {
      const list = await api.request(`/financials/schedule-approvals?status=${status}&${scope.query}`);
      const current = await api.request(`/financials/settings?${scope.query}`);
      if (!isCurrent()) return;
      setRows(list);
      setSelected((cur) => (list.some((r) => r.version.version_id === cur) ? cur : list[0]?.version.version_id || ''));
      setSettings(current);
    } catch (err) { if (isCurrent()) { toast.error(errorText(err)); setRows([]); } }
  }, [filter, scope.ready, scope.query, nextRequest]);
  useEffect(() => { load(); }, [load]);
  if (!['super_admin', 'iso'].includes(user?.role)) return <FinanceOnly testid="schedule-approvals-denied" />;
  const togglePolicy = async () => {
    try {
      setSettings(await api.request('/financials/settings', { method: 'PUT', body: JSON.stringify({
        ...scope.body, assigned_schedule_maker_checker: !settings?.assigned_schedule_maker_checker }) }));
      toast.success('Approval policy updated');
      load();
    } catch (err) { toast.error(errorText(err)); }
  };
  const row = rows.find((r) => r.version.version_id === selected);
  return (
    <div className="space-y-8" data-testid="schedule-approvals-page">
      <PageHeader title="Schedule Approval"
                  subtitle="Migrated and proposed economics become live only after an authorised approval. Native schedules always need evidence and a different approver.">
        <TenantSelect scope={scope} testid="schedule-approvals-tenant-select" />
        <label className="flex h-10 items-center gap-2 rounded-full border px-4 text-sm">
          <input type="checkbox" checked={Boolean(settings?.assigned_schedule_maker_checker)} onChange={togglePolicy}
                 data-testid="assigned-maker-checker-toggle" />
          Maker/checker for assigned schedules
        </label>
      </PageHeader>
      <div className="flex flex-wrap gap-2" data-testid="schedule-approval-filters">
        {FILTERS.map(([key, label]) => (
          <button key={key} type="button" onClick={() => setFilter(key)} data-testid={`filter-${key}`}
                  className={`h-9 rounded-full px-4 text-sm transition-colors ${filter === key ? 'bg-primary text-primary-foreground' : 'border hover:bg-muted'}`}>
            {label}
          </button>
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-[22rem_1fr]">
        <Card testid="schedule-queue" title="Queue" aside={<span className="text-xs text-muted-foreground">{rows.length}</span>}>
          <ul className="divide-y">
            {rows.map((r) => (
              <li key={r.version.version_id}>
                <button type="button" onClick={() => setSelected(r.version.version_id)}
                        data-testid={`queue-item-${r.version.version_id}`}
                        className={`w-full px-5 py-3 text-left transition-colors hover:bg-muted/50 ${selected === r.version.version_id ? 'bg-muted/60' : ''}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate text-sm font-medium">{r.schedule.name}</p>
                    <StatusPill value={r.verification} />
                  </div>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    v{r.version.version} · {r.schedule.schedule_type === 'native_processor' ? 'Native' : `Assigned → ${r.assignee_name || '—'}`} · {r.source}
                  </p>
                </button>
              </li>
            ))}
            {!rows.length && <li className="px-5 py-10 text-center text-sm text-muted-foreground" data-testid="schedule-queue-empty">Nothing in this queue.</li>}
          </ul>
        </Card>
        {row ? <Detail row={row} user={user} onChanged={load} /> : (
          <Card testid="schedule-detail-empty"><p className="px-5 py-16 text-center text-sm text-muted-foreground">Select a schedule version to review.</p></Card>
        )}
      </div>
    </div>
  );
}
